<template>
  <div
    v-if="getIsClient() && (isLoading || (!content.length && status === 'idle'))"
    class="flex flex-col"
  >
    <LcSkeleton height="30rem" radius="medium" />
    <LcSkeleton class="mt-200 mb-100" height="1.625rem" radius="medium" width="40%" />
    <LcSkeleton class="mb-050" height="0.75rem" radius="medium" />
    <LcSkeleton class="mb-050" height="0.75rem" radius="medium" />
    <LcSkeleton class="mb-050" height="0.75rem" radius="medium" width="40%" />
  </div>

  <div v-else class="info-prizes">
    <LcTabs
      v-if="tabs.length > 0 && tierTableProps"
      v-bind="{ tabs }"
      adaptive-height
      class="mb-300"
    >
      <template #tab="{ tab }">
        <div class="flex flex-col items-center justify-center text-center">
          <span :class="{ 'text-xs': !!tab.data.tierMultiplier }">
            {{
              tab.name === 'DEFAULT'
                ? $t(`info.prizes.tiertab.default.${lotteryType}`)
                : getTranslatedAddOnTitle(tab.data.addOnName)
            }}
          </span>

          <span v-if="tab.data.tierMultiplier">
            {{
              $t('tiertable.multiplier', {
                value: tab.data.tierMultiplier,
              })
            }}
          </span>
        </div>
      </template>

      <template #tab-panel="{ tab }">
        <TierTable
          v-if="tab?.data?.prizeTiers"
          v-bind="tierTableProps"
          :class="tabs.length > 1 ? '-mt-050' : '-mt-150'"
          :grouped-prize-tiers="getGroupedPrizeTiers(tab.data.prizeTiers)"
        />
      </template>
    </LcTabs>

    <section v-if="content.length">
      <template v-for="item in content" :key="item?.sys.id">
        <h2 v-if="item.caption" class="headline-h4-headline mb-100">
          {{ item.caption }}
        </h2>

        <ContentfulRenderer class="content" :data="item.content" />
      </template>
    </section>
    <NoResults
      v-else
      :button-text="$t('info.noresults.button')"
      :headline="$t('info.noresults.headline')"
      :subline="$t('info.noresults.subline')"
    />
  </div>
</template>

<script lang="ts" setup>
import type { ContentBlock } from '#content/shared/@types/generated/cms/graphql-schema-types'
import type { TierTablePrizeTier, TierTableProps } from '#product/app/components/TierTable.vue'

import {
  type ByAddOnSelected,
  type ByMultiplierDeselected,
  type ByMultiplierSelected,
  type ByMultiplierValueMatched,
  type PrizeTier,
  type PrizeTierCategoryRequirements,
  PrizeTierCategoryRequirementsPayloadType,
} from '#api/shared/@types/generated/backend/graphql-schema-types'

import type { InfoPageProps } from '../types'

const hasAddOnName = (
  requirement: PrizeTierCategoryRequirements,
): requirement is
  | ByAddOnSelected
  | ByMultiplierDeselected
  | ByMultiplierSelected
  | ByMultiplierValueMatched =>
  [
    PrizeTierCategoryRequirementsPayloadType.ByAddonSelected,
    PrizeTierCategoryRequirementsPayloadType.ByMultiplierDeselected,
    PrizeTierCategoryRequirementsPayloadType.ByMultiplierSelected,
    PrizeTierCategoryRequirementsPayloadType.ByMultiplierValueMatched,
  ].includes(requirement.type)

const isMultiplierRequirement = (
  requirement: PrizeTierCategoryRequirements,
): requirement is ByMultiplierSelected | ByMultiplierValueMatched =>
  [
    PrizeTierCategoryRequirementsPayloadType.ByMultiplierSelected,
    PrizeTierCategoryRequirementsPayloadType.ByMultiplierValueMatched,
  ].includes(requirement.type)

const { id, isVisible } = defineProps<InfoPageProps>()

const { t } = useI18n()
let playSlip: Maybe<ReturnType<typeof usePlaySlip>> = null

let lotteryType: Maybe<string> = null

try {
  playSlip = usePlaySlip()
  lotteryType = useLotteryType(playSlip.lotteryId).lotteryType.value
} catch {
  playSlip = null
}

const { data, execute, pending, status } = await useContent().getPrizes(id, {
  immediate: false,
})
const isLoading = useMinimumLoading(pending)

const addOn = computed(() => {
  if (!playSlip) return null
  const { addOns, afterPurchaseAddOns } = playSlip

  return [...addOns.value, ...afterPurchaseAddOns.value].find(isNotPerPlay) ?? null
})
const content = computed(() => (data.value?.contentBlocksCollection?.items as ContentBlock[]) || [])
const prizeTierCategories = computed(() => playSlip?.config.value?.prizeTierCategories ?? [])
const tabs = computed(() =>
  prizeTierCategories.value
    .filter(({ group }) => group === 'DEFAULT')
    .map(({ categoryRequirements, name, prizeTiers }, index) => ({
      data: {
        addOnName: hasAddOnName(categoryRequirements[0])
          ? categoryRequirements[0].addOnName
          : addOn.value?.name,
        prizeTiers,
        tierMultiplier: tierMultipliers.value[index],
      },
      name,
    })),
)
const tierMultipliers = computed(() =>
  prizeTierCategories.value.reduce<string[]>((acc, { categoryRequirements, name }, index) => {
    if (name === 'DEFAULT') return [...acc, '']

    let values: string[] = []

    if (addOn.value?.options.length) {
      values.push(addOn.value.options[index - 1])
    } else {
      values = categoryRequirements
        .filter(isMultiplierRequirement)
        .map(({ value }) => String(value))
    }

    return [...acc, ...values]
  }, []),
)
const tierTableProps = computed<Maybe<Omit<TierTableProps, 'groupedPrizeTiers'>>>(() => {
  if (!playSlip) return null
  const { addOns, licensedTerritory, lotteryId } = playSlip

  return {
    addOns: addOns.value,
    licensedTerritory,
    lotteryId,
    lotteryType,
    type: 'prizes',
  }
})

const getGroupedPrizeTiers = (prizeTiers: Omit<PrizeTier, 'odds'>[]) => [
  {
    prizeTiers: prizeTiers.map<TierTablePrizeTier>((prizeTier) => ({
      ...prizeTier,
      prize: prizeTier.amount,
    })),
  },
]

const getTranslatedAddOnTitle = (name?: string) => {
  if (!name || !playSlip) return null
  const { isNumberLottery, isPickLottery } = useLotteryType(playSlip.lotteryId)

  const addOnTranslation = t(`lottery.${playSlip.lotteryId}.${name}`)
  if (isNumberLottery.value) return addOnTranslation
  if (isPickLottery.value) {
    return t('info.prizes.tiertab.addon', {
      addOn: addOnTranslation,
    })
  }
  return null
}

watchImmediate(
  () => isVisible,
  async (newValue) => {
    if (!newValue || data.value) return
    await execute()
  },
)

onServerPrefetch(execute)
</script>

<style lang="scss" scoped>
.info-prizes {
  h4:not(:first-of-type) {
    margin-top: token('size/space/gap/300');
  }

  /* stylelint-disable-next-line selector-class-pattern */
  :deep(.lc-tabs__tab) {
    height: 2.375rem;
    padding: token('size/space/gap/none');
  }

  :deep(.content) {
    dl,
    ol,
    ul {
      margin-bottom: token('size/space/gap/200');
    }

    ol,
    ul {
      padding-left: token('size/space/gap/200');
      list-style: unset;

      ::marker {
        color: token('color/text/accent/accent/placeholder');
      }
    }
  }

  .text-xs {
    font-size: 0.5625rem;
    line-height: 1.28;
  }
}
</style>
